import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const sections = [
  { id: 'introduction', title: 'Introduction' },
  { id: 'info-collect', title: 'Information We Collect' },
  { id: 'info-not-collect', title: 'Information We Do Not Collect' },
  { id: 'how-use', title: 'How We Use Your Information' },
  { id: 'data-storage', title: 'Data Storage & Security' },
  { id: 'data-sharing', title: 'Data Sharing' },
  { id: 'data-retention', title: 'Data Retention' },
  { id: 'your-rights', title: 'Your Rights' },
  { id: 'children', title: "Children's Privacy" },
  { id: 'changes', title: 'Changes to This Policy' },
  { id: 'contact', title: 'Contact Us' },
];

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(s => ({
        id: s.id,
        el: document.getElementById(s.id),
      }));
      
      for (const section of sectionElements) {
        if (section.el) {
          const rect = section.el.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -100;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Privacy Policy – DivExp</title>
        <meta name="description" content="Privacy Policy for DivExp - Learn how we collect, use, and protect your personal information." />
      </Helmet>

      {/* Hero Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.iconWrapper}>
            <svg style={styles.shieldIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1 style={styles.title}>Privacy Policy</h1>
          <p style={styles.subtitle}>for DivExp</p>
          <div style={styles.lastUpdated}>
            <span style={styles.updateIcon}>📅</span>
            Last Updated: February 2, 2026
          </div>
        </div>
      </header>

      <div style={styles.mainContent}>
        {/* Table of Contents - Desktop Sidebar */}
        <nav style={styles.tocSidebar}>
          <div style={styles.tocSticky}>
            <h3 style={styles.tocTitle}>Contents</h3>
            <ul style={styles.tocList}>
              {sections.map((section) => (
                <li key={section.id}>
                  <button
                    onClick={() => scrollToSection(section.id)}
                    style={{
                      ...styles.tocLink,
                      ...(activeSection === section.id ? styles.tocLinkActive : {}),
                    }}
                  >
                    {section.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Main Policy Content */}
        <main style={styles.content}>
          {/* Introduction */}
          <section id="introduction" style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <span style={styles.sectionIcon}>👋</span>
              Introduction
            </h2>
            <p style={styles.paragraph}>
              DivExp ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our mobile application.
            </p>
          </section>

          {/* Information We Collect */}
          <section id="info-collect" style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <span style={styles.sectionIcon}>📋</span>
              Information We Collect
            </h2>
            
            <div style={styles.infoCard}>
              <h3 style={styles.cardTitle}>
                <span style={styles.cardIcon}>👤</span>
                Account Information
              </h3>
              <ul style={styles.list}>
                <li style={styles.listItem}>
                  <span style={styles.bullet}>•</span>
                  Name (from your Google or Apple account, or manually entered)
                </li>
                <li style={styles.listItem}>
                  <span style={styles.bullet}>•</span>
                  Email address (for authentication)
                </li>
                <li style={styles.listItem}>
                  <span style={styles.bullet}>•</span>
                  User ID (unique identifier for your account)
                </li>
              </ul>
            </div>

            <div style={styles.infoCard}>
              <h3 style={styles.cardTitle}>
                <span style={styles.cardIcon}>✏️</span>
                User-Generated Content
              </h3>
              <ul style={styles.list}>
                <li style={styles.listItem}>
                  <span style={styles.bullet}>•</span>
                  Expense descriptions and amounts
                </li>
                <li style={styles.listItem}>
                  <span style={styles.bullet}>•</span>
                  Group names
                </li>
                <li style={styles.listItem}>
                  <span style={styles.bullet}>•</span>
                  Friend connections
                </li>
              </ul>
            </div>

            <div style={styles.infoCard}>
              <h3 style={styles.cardTitle}>
                <span style={styles.cardIcon}>📱</span>
                Technical Information
                <span style={styles.conditionalBadge}>(only when submitting feedback)</span>
              </h3>
              <ul style={styles.list}>
                <li style={styles.listItem}>
                  <span style={styles.bullet}>•</span>
                  Device model
                </li>
                <li style={styles.listItem}>
                  <span style={styles.bullet}>•</span>
                  iOS version
                </li>
                <li style={styles.listItem}>
                  <span style={styles.bullet}>•</span>
                  App version
                </li>
              </ul>
            </div>
          </section>

          {/* Information We Do Not Collect */}
          <section id="info-not-collect" style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <span style={styles.sectionIcon}>🚫</span>
              Information We Do Not Collect
            </h2>
            <div style={styles.doNotCollectGrid}>
              {[
                { icon: '💳', text: 'Payment or credit card information (handled by Apple)' },
                { icon: '📍', text: 'Location data' },
                { icon: '📇', text: 'Contacts' },
                { icon: '📷', text: 'Photos or media' },
                { icon: '❤️', text: 'Health data' },
                { icon: '🌐', text: 'Browsing history' },
              ].map((item, index) => (
                <div key={index} style={styles.doNotCollectItem}>
                  <span style={styles.doNotCollectIcon}>{item.icon}</span>
                  <span style={styles.doNotCollectText}>{item.text}</span>
                </div>
              ))}
            </div>
          </section>

          {/* How We Use Your Information */}
          <section id="how-use" style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <span style={styles.sectionIcon}>⚙️</span>
              How We Use Your Information
            </h2>
            <p style={styles.paragraph}>We use your information to:</p>
            <ul style={styles.usageList}>
              <li style={styles.usageItem}>
                <span style={styles.checkIcon}>✓</span>
                Create and manage your account
              </li>
              <li style={styles.usageItem}>
                <span style={styles.checkIcon}>✓</span>
                Enable expense splitting functionality
              </li>
              <li style={styles.usageItem}>
                <span style={styles.checkIcon}>✓</span>
                Display your name to group members
              </li>
              <li style={styles.usageItem}>
                <span style={styles.checkIcon}>✓</span>
                Process and respond to feedback
              </li>
              <li style={styles.usageItem}>
                <span style={styles.checkIcon}>✓</span>
                Improve our services
              </li>
            </ul>
          </section>

          {/* Data Storage and Security */}
          <section id="data-storage" style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <span style={styles.sectionIcon}>🔒</span>
              Data Storage and Security
            </h2>
            <div style={styles.securityCard}>
              <p style={styles.paragraph}>
                Your data is stored securely using <strong>Google Firebase</strong> services. We implement appropriate security measures to protect against unauthorized access, alteration, or destruction of your data.
              </p>
            </div>
          </section>

          {/* Data Sharing */}
          <section id="data-sharing" style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <span style={styles.sectionIcon}>🤝</span>
              Data Sharing
            </h2>
            <div style={styles.highlightBox}>
              <p style={styles.highlightText}>
                <strong>We do not sell your personal information.</strong>
              </p>
            </div>
            <p style={styles.paragraph}>We may share data with:</p>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                <span style={styles.bullet}>•</span>
                <strong>Firebase (Google)</strong> for authentication and data storage
              </li>
              <li style={styles.listItem}>
                <span style={styles.bullet}>•</span>
                <strong>Service providers</strong> who assist in app operations
              </li>
            </ul>
            <p style={styles.note}>
              All third parties are bound by confidentiality obligations.
            </p>
          </section>

          {/* Data Retention */}
          <section id="data-retention" style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <span style={styles.sectionIcon}>🗄️</span>
              Data Retention
            </h2>
            <p style={styles.paragraph}>
              We retain your data for as long as your account is active. You may delete your account at any time through the app's <strong>Profile settings</strong>, which will permanently remove your data.
            </p>
          </section>

          {/* Your Rights */}
          <section id="your-rights" style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <span style={styles.sectionIcon}>⚖️</span>
              Your Rights
            </h2>
            <p style={styles.paragraph}>You have the right to:</p>
            <div style={styles.rightsGrid}>
              {[
                { icon: '👁️', title: 'Access', desc: 'Access your personal data' },
                { icon: '✏️', title: 'Correct', desc: 'Correct inaccurate data' },
                { icon: '🗑️', title: 'Delete', desc: 'Delete your account and data' },
                { icon: '📤', title: 'Export', desc: 'Export your data' },
              ].map((right, index) => (
                <div key={index} style={styles.rightCard}>
                  <span style={styles.rightIcon}>{right.icon}</span>
                  <h4 style={styles.rightTitle}>{right.title}</h4>
                  <p style={styles.rightDesc}>{right.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Children's Privacy */}
          <section id="children" style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <span style={styles.sectionIcon}>👶</span>
              Children's Privacy
            </h2>
            <div style={styles.warningBox}>
              <p style={styles.paragraph}>
                DivExp is <strong>not intended for children under 13</strong>. We do not knowingly collect information from children under 13.
              </p>
            </div>
          </section>

          {/* Changes to This Policy */}
          <section id="changes" style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <span style={styles.sectionIcon}>📝</span>
              Changes to This Policy
            </h2>
            <p style={styles.paragraph}>
              We may update this Privacy Policy from time to time. We will notify you of changes by updating the "Last Updated" date at the top of this page.
            </p>
          </section>

          {/* Contact Us */}
          <section id="contact" style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <span style={styles.sectionIcon}>📧</span>
              Contact Us
            </h2>
            <p style={styles.paragraph}>
              If you have questions about this Privacy Policy, contact us at:
            </p>
            <div style={styles.contactCard}>
              <a href="mailto:truth.ai.tech@gmail.com" style={styles.emailLink}>
                <span style={styles.emailIcon}>✉️</span>
                truth.ai.tech@gmail.com
              </a>
            </div>
          </section>

          {/* Footer */}
          <footer style={styles.footer}>
            <div style={styles.divider}></div>
            <p style={styles.copyright}>© 2026 DivExp. All rights reserved.</p>
            <Link to="/" style={styles.backLink}>
              ← Back to Home
            </Link>
          </footer>
        </main>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
  },
  header: {
    background: 'linear-gradient(135deg, #007bff 0%, #00c6ff 100%)',
    padding: '60px 20px 80px',
    position: 'relative',
    overflow: 'hidden',
  },
  headerContent: {
    maxWidth: '800px',
    margin: '0 auto',
    textAlign: 'center',
    position: 'relative',
    zIndex: 1,
  },
  iconWrapper: {
    width: '80px',
    height: '80px',
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    backdropFilter: 'blur(10px)',
  },
  shieldIcon: {
    width: '44px',
    height: '44px',
    color: 'white',
  },
  title: {
    fontSize: '42px',
    fontWeight: '800',
    color: 'white',
    margin: '0 0 8px',
    letterSpacing: '-1px',
  },
  subtitle: {
    fontSize: '20px',
    color: 'rgba(255, 255, 255, 0.9)',
    margin: '0 0 24px',
    fontWeight: '500',
  },
  lastUpdated: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'rgba(255, 255, 255, 0.2)',
    padding: '10px 20px',
    borderRadius: '30px',
    color: 'white',
    fontSize: '14px',
    fontWeight: '500',
    backdropFilter: 'blur(10px)',
  },
  updateIcon: {
    fontSize: '16px',
  },
  mainContent: {
    maxWidth: '1200px',
    margin: '-40px auto 0',
    padding: '0 20px 60px',
    display: 'grid',
    gridTemplateColumns: '240px 1fr',
    gap: '40px',
    position: 'relative',
    zIndex: 2,
  },
  tocSidebar: {
    display: 'none',
  },
  tocSticky: {
    position: 'sticky',
    top: '100px',
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  },
  tocTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    margin: '0 0 16px',
  },
  tocList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  tocLink: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '10px 14px',
    border: 'none',
    background: 'transparent',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#64748b',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontWeight: '500',
  },
  tocLinkActive: {
    background: '#e0f2fe',
    color: '#007bff',
    fontWeight: '600',
  },
  content: {
    background: 'white',
    borderRadius: '24px',
    padding: '40px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    gridColumn: '1 / -1',
  },
  section: {
    marginBottom: '48px',
    scrollMarginTop: '120px',
  },
  sectionTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    paddingBottom: '12px',
    borderBottom: '2px solid #f1f5f9',
  },
  sectionIcon: {
    fontSize: '24px',
  },
  paragraph: {
    fontSize: '16px',
    lineHeight: '1.8',
    color: '#475569',
    margin: '0 0 16px',
  },
  infoCard: {
    background: '#f8fafc',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '16px',
    border: '1px solid #e2e8f0',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#334155',
    margin: '0 0 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  cardIcon: {
    fontSize: '20px',
  },
  conditionalBadge: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#64748b',
    background: '#e2e8f0',
    padding: '4px 10px',
    borderRadius: '20px',
    marginLeft: '8px',
  },
  list: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  listItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    fontSize: '15px',
    color: '#475569',
    marginBottom: '10px',
    lineHeight: '1.6',
  },
  bullet: {
    color: '#007bff',
    fontWeight: 'bold',
    fontSize: '18px',
    lineHeight: '1.4',
  },
  doNotCollectGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '12px',
  },
  doNotCollectItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 20px',
    background: '#fef2f2',
    borderRadius: '12px',
    border: '1px solid #fecaca',
  },
  doNotCollectIcon: {
    fontSize: '20px',
    opacity: 0.6,
  },
  doNotCollectText: {
    fontSize: '14px',
    color: '#991b1b',
    fontWeight: '500',
  },
  usageList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  usageItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '15px',
    color: '#475569',
    marginBottom: '12px',
    padding: '12px 16px',
    background: '#f0fdf4',
    borderRadius: '10px',
    border: '1px solid #bbf7d0',
  },
  checkIcon: {
    color: '#16a34a',
    fontWeight: 'bold',
    fontSize: '18px',
  },
  securityCard: {
    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid #bfdbfe',
  },
  highlightBox: {
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    borderRadius: '12px',
    padding: '20px 24px',
    marginBottom: '20px',
    border: '1px solid #bbf7d0',
  },
  highlightText: {
    fontSize: '16px',
    color: '#166534',
    margin: 0,
    fontWeight: '500',
  },
  note: {
    fontSize: '14px',
    color: '#64748b',
    fontStyle: 'italic',
    marginTop: '16px',
    padding: '12px 16px',
    background: '#f8fafc',
    borderRadius: '8px',
    borderLeft: '3px solid #007bff',
  },
  rightsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginTop: '20px',
  },
  rightCard: {
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    borderRadius: '16px',
    padding: '24px',
    textAlign: 'center',
    border: '1px solid #e2e8f0',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  rightIcon: {
    fontSize: '32px',
    display: 'block',
    marginBottom: '12px',
  },
  rightTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#334155',
    margin: '0 0 8px',
  },
  rightDesc: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0,
  },
  warningBox: {
    background: '#fefce8',
    borderRadius: '12px',
    padding: '20px 24px',
    border: '1px solid #fef08a',
  },
  contactCard: {
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    borderRadius: '16px',
    padding: '32px',
    textAlign: 'center',
    border: '1px solid #e2e8f0',
    marginTop: '20px',
  },
  emailLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '18px',
    fontWeight: '600',
    color: '#007bff',
    textDecoration: 'none',
    padding: '16px 32px',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 123, 255, 0.15)',
    transition: 'all 0.2s ease',
  },
  emailIcon: {
    fontSize: '22px',
  },
  footer: {
    marginTop: '60px',
    textAlign: 'center',
  },
  divider: {
    height: '1px',
    background: 'linear-gradient(90deg, transparent, #e2e8f0, transparent)',
    marginBottom: '24px',
  },
  copyright: {
    fontSize: '14px',
    color: '#64748b',
    margin: '0 0 16px',
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '15px',
    color: '#007bff',
    textDecoration: 'none',
    fontWeight: '600',
    padding: '10px 20px',
    background: '#eff6ff',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
  },

  // Media query styles will be applied via CSS
  '@media (min-width: 1024px)': {
    tocSidebar: {
      display: 'block',
    },
    content: {
      gridColumn: '2',
    },
  },
};

// Add responsive styles via CSS-in-JS approach
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @media (min-width: 1024px) {
    .privacy-toc-sidebar {
      display: block !important;
    }
    .privacy-content {
      grid-column: 2 !important;
    }
  }
  
  .privacy-toc-link:hover {
    background: #f1f5f9;
    color: #334155;
  }
  
  .privacy-email-link:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.25);
  }
  
  .privacy-back-link:hover {
    background: #dbeafe;
  }
  
  .privacy-right-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

if (typeof document !== 'undefined' && !document.getElementById('privacy-policy-styles')) {
  styleSheet.id = 'privacy-policy-styles';
  document.head.appendChild(styleSheet);
}

export default PrivacyPolicy;
